import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { brandColors } from '@/design';

// ─── date helpers ─────────────────────────────────────────────────────────────

export function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseDateInputValue(raw: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return null;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(parsed.getTime())) return null;
  const result = new Date(parsed);
  result.setHours(0, 0, 0, 0);
  return result;
}

function normalize(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Returns 0 = Mon … 6 = Sun (ISO week order) */
function firstWeekdayOfMonth(year: number, month: number): number {
  return (new Date(year, month, 1).getDay() + 6) % 7;
}

// ─── constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const PANEL_W = 296;
const PANEL_H = 360; // approximate — used only for viewport-edge detection
const GAP = 8;

// ─── CalendarDropdown ─────────────────────────────────────────────────────────

interface DropdownProps {
  date: Date;
  onChange: (date: Date) => void;
  min?: Date;
  max?: Date;
  anchorEl: HTMLElement;
  onClose: () => void;
}

function CalendarDropdown({ date, onChange, min, max, anchorEl, onClose }: DropdownProps) {
  const today = normalize(new Date());
  const selectedDate = normalize(date);
  const [viewYear, setViewYear] = useState(date.getFullYear());
  const [viewMonth, setViewMonth] = useState(date.getMonth());
  const panelRef = useRef<HTMLDivElement>(null);

  const minDate = min ? normalize(min) : null;
  const maxDate = max ? normalize(max) : null;

  // ── position ──────────────────────────────────────────────────────────────
  const rect = anchorEl.getBoundingClientRect();
  let top = rect.bottom + GAP;
  let left = rect.left;

  // Flip above if panel would overflow below
  if (top + PANEL_H > window.innerHeight - 12) top = rect.top - PANEL_H - GAP;
  // Nudge left if panel would overflow right edge
  if (left + PANEL_W > window.innerWidth - 12) left = rect.right - PANEL_W;
  left = Math.max(12, left);

  // ── close on outside click / Escape ───────────────────────────────────────
  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !anchorEl.contains(e.target as Node)
      ) onClose();
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [onClose, anchorEl]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ── month navigation ──────────────────────────────────────────────────────
  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  }

  // ── day selection ─────────────────────────────────────────────────────────
  function selectDay(dayNum: number) {
    const picked = normalize(new Date(viewYear, viewMonth, dayNum));
    if (minDate && picked < minDate) return;
    if (maxDate && picked > maxDate) return;
    onChange(picked);
    onClose();
  }

  function goToToday() {
    const t = normalize(new Date());
    if (minDate && t < minDate) return;
    if (maxDate && t > maxDate) return;
    setViewYear(t.getFullYear());
    setViewMonth(t.getMonth());
    onChange(t);
    onClose();
  }

  // ── calendar grid math ────────────────────────────────────────────────────
  const monthDays = daysInMonth(viewYear, viewMonth);
  const firstWD = firstWeekdayOfMonth(viewYear, viewMonth);
  const prevY = viewMonth === 0 ? viewYear - 1 : viewYear;
  const prevM = viewMonth === 0 ? 11 : viewMonth - 1;
  const prevMonthDays = daysInMonth(prevY, prevM);
  const totalCells = Math.ceil((firstWD + monthDays) / 7) * 7;
  const todayOutOfRange = Boolean((minDate && today < minDate) || (maxDate && today > maxDate));

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-[9999] select-none"
      style={{ top, left, width: PANEL_W }}
    >
      {/* Card shell */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'var(--color-surface-elevated, var(--color-surface))',
          border: '1px solid var(--color-border)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
        }}
      >

        {/* ── header: month + nav ── */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid var(--color-border)' }}
        >
          <button
            onClick={prevMonth}
            className="p-1.5 rounded-lg"
            style={{ color: 'var(--color-text-secondary)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-surface)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = ''; }}
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>

          <span
            className="font-bold text-sm tracking-tight"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {MONTH_NAMES[viewMonth]} {viewYear}
          </span>

          <button
            onClick={nextMonth}
            className="p-1.5 rounded-lg"
            style={{ color: 'var(--color-text-secondary)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-surface)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = ''; }}
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* ── day-of-week labels ── */}
        <div className="grid grid-cols-7 px-4 pt-4 pb-2">
          {DAY_LABELS.map((label, i) => (
            <div
              key={i}
              className="text-center text-[10px] font-semibold uppercase tracking-wider py-0.5"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {label}
            </div>
          ))}
        </div>

        {/* ── day grid ── */}
        <div className="grid grid-cols-7 px-4 pb-4 gap-y-0.5">
          {Array.from({ length: totalCells }, (_, i) => {
            const dayNum = i - firstWD + 1;
            const isCurrent = dayNum >= 1 && dayNum <= monthDays;

            if (!isCurrent) {
              const fadedDay = i < firstWD
                ? prevMonthDays - firstWD + i + 1
                : dayNum - monthDays;
              return (
                // Overflow days: same gray shade as upcoming current-month days
                <div
                  key={i}
                  className="text-center text-sm py-2"
                  style={{ color: 'var(--color-text-secondary)', opacity: 0.45 }}
                >
                  {fadedDay}
                </div>
              );
            }

            const cellDate = normalize(new Date(viewYear, viewMonth, dayNum));
            const isSelected = cellDate.getTime() === selectedDate.getTime();
            const isToday = cellDate.getTime() === today.getTime();
            const isPast = cellDate < today;
            const isDisabled = Boolean(
              (minDate && cellDate < minDate) || (maxDate && cellDate > maxDate)
            );

            // Three-tier color hierarchy for current-month days:
            //   past  → full white (primary), opacity 1
            //   today → teal
            //   future → white but at ~65% opacity via a span, so the button
            //            hover background stays crisp while the number reads lighter
            const isFuture = !isPast && !isToday;
            const futureOpacity = isFuture && !isSelected && !isDisabled ? 0.58 : undefined;

            function dayColor() {
              if (isSelected) return '#fff';
              if (isToday && !isDisabled) return brandColors.teal;
              return 'var(--color-text-primary)';
            }

            return (
              <button
                key={i}
                onClick={() => !isDisabled && selectDay(dayNum)}
                disabled={isDisabled}
                className={[
                  'text-center text-sm py-2 rounded-lg transition-colors duration-100',
                  isDisabled ? 'opacity-25 cursor-not-allowed' : 'cursor-pointer',
                  !isSelected && !isDisabled ? 'hover:bg-[var(--color-surface)]' : '',
                ].join(' ')}
                style={{
                  fontWeight: isSelected || isToday ? 600 : 400,
                  background: isSelected ? brandColors.teal : 'transparent',
                  color: dayColor(),
                  outline: isToday && !isSelected ? `1.5px solid ${brandColors.teal}55` : 'none',
                  outlineOffset: '-1.5px',
                }}
              >
                <span style={futureOpacity !== undefined ? { opacity: futureOpacity } : undefined}>
                  {dayNum}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── footer: cancel + today ── */}
        <div
          className="flex items-center justify-between px-5 py-3.5"
          style={{ borderTop: '1px solid var(--color-border)' }}
        >
          <button
            onClick={onClose}
            className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
            style={{ color: 'var(--color-text-secondary)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-surface)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = ''; }}
          >
            Cancel
          </button>
          <button
            onClick={goToToday}
            disabled={todayOutOfRange}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-30"
            style={{ color: brandColors.teal }}
            onMouseEnter={e => {
              if (!todayOutOfRange) e.currentTarget.style.background = `${brandColors.teal}1a`;
            }}
            onMouseLeave={e => { e.currentTarget.style.background = ''; }}
          >
            Today
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── DatePickerButton (public API) ────────────────────────────────────────────

interface DatePickerButtonProps {
  date: Date;
  onChange: (date: Date) => void;
  min?: Date;
  max?: Date;
  /**
   * "full"  – teal calendar icon + formatted date text (journal header, etc.)
   * "icon"  – calendar icon only, matches toolbar icon-button style
   */
  variant?: 'full' | 'icon';
  /** Override the default date label. Defaults to "9 Mar 2026" locale style. */
  formatDate?: (date: Date) => string;
  className?: string;
}

export function DatePickerButton({
  date,
  onChange,
  min,
  max,
  variant = 'full',
  formatDate,
  className = '',
}: DatePickerButtonProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const label = formatDate
    ? formatDate(date)
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  function toggle() { setOpen(prev => !prev); }
  function close() { setOpen(false); }

  if (variant === 'icon') {
    return (
      <>
        <button
          ref={triggerRef}
          onClick={toggle}
          className={`p-2 rounded-lg transition-colors shrink-0 ${className}`}
          style={{
            background: open ? `${brandColors.teal}1a` : 'var(--color-surface)',
            color: open ? brandColors.teal : 'var(--color-text-secondary)',
            border: `1px solid ${open ? `${brandColors.teal}55` : 'var(--color-border)'}`,
          }}
          title={`Jump to date · ${label}`}
          aria-label="Pick date"
          aria-expanded={open}
        >
          <CalendarDays size={15} />
        </button>
        {open && triggerRef.current && (
          <CalendarDropdown
            date={date}
            onChange={onChange}
            min={min}
            max={max}
            anchorEl={triggerRef.current}
            onClose={close}
          />
        )}
      </>
    );
  }

  // full variant
  return (
    <>
      <button
        ref={triggerRef}
        onClick={toggle}
        className={`
          inline-flex items-center gap-2 rounded-xl px-3.5 py-2
          transition-all duration-150 hover:brightness-95 active:scale-[.98]
          ${className}
        `}
        style={{
          background: 'var(--color-surface)',
          border: `1px solid ${open ? brandColors.teal + '66' : 'var(--color-border)'}`,
          color: 'var(--color-text-primary)',
          minWidth: '148px',
        }}
        title="Pick date"
        aria-label="Pick date"
        aria-expanded={open}
      >
        <CalendarDays size={14} style={{ color: brandColors.teal, flexShrink: 0 }} />
        <span className="text-sm font-semibold tracking-tight">{label}</span>
      </button>
      {open && triggerRef.current && (
        <CalendarDropdown
          date={date}
          onChange={onChange}
          min={min}
          max={max}
          anchorEl={triggerRef.current}
          onClose={close}
        />
      )}
    </>
  );
}
